import React, { useState } from 'react'
import { CardFrame } from '../../../ui/CardFrame/CardFrame.jsx'
import { RatingBars, RevealCover, RatingNote } from '../../../ui/RatingBars/RatingBars.jsx'
import { useRating } from '../../../hooks/useRating.js'

/**
 * SessionRatingCard — catalog #13, S10 commit-then-reveal.
 *
 * Each party independently selects a 1–5 score and an optional 280-char note.
 * The partner's score is hidden (RevealCover) until the server confirms both
 * have rated (partner.revealedAt is non-null). The server is authoritative —
 * the frontend never synthesizes a reveal; it only acts on revealedAt from
 * the API response.
 *
 * Reveal animation:
 *   - CSS-driven via rb-reveal / rb-reveal--revealed classes (ui.css).
 *   - prefers-reduced-motion: transition-duration collapses to a fade (80ms)
 *     via the media query in ui.css — no JS involvement needed.
 *
 * Phase G follow-up: live-reveal push is deferred (SEC-fu-F1, #534).
 *   The card re-fetches after submitRating returns. A partner who submitted
 *   concurrently will have their reveal appear on the next poll / page load.
 * // Phase G follow-up: live reveal push — wire onRatingRevealed subscription
 * //   once SEC-fu-F1 authz work lands.
 *
 * Props:
 *   sessionId   — required to fetch / submit the rating pair.
 *   isReport    — true if the viewer is the report (affects row labelling).
 *   disabled    — mirrors session lock state; hides the submit form.
 */
export function SessionRatingCard({ sessionId, isReport = true, disabled = false }) {
  const { ratingPair, loading, submitRating } = useRating(sessionId, { enabled: !!sessionId })

  const [pendingScore, setPendingScore] = useState(0)
  const [pendingNote, setPendingNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  const me = ratingPair?.me ?? { submitted: false }
  const partner = ratingPair?.partner ?? { submitted: false }
  const revealed = partner.revealedAt != null

  // The report's row is always first; manager's second.
  const myLabel = isReport ? 'You (report)' : 'You (manager)'
  const partnerLabel = isReport ? 'Manager' : 'Report'

  async function handleSubmit() {
    if (!pendingScore) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      await submitRating(pendingScore, pendingNote || null)
    } catch (err) {
      setSubmitError(err?.message ?? 'Failed to submit rating.')
    } finally {
      setSubmitting(false)
    }
  }

  const state = disabled ? 'locked' : me.submitted ? 'filled' : 'empty'

  return (
    <CardFrame
      kind="session"
      icon="◑"
      iconClass="moss"
      title="Session rating"
      prompt="How useful was this 1:1?"
      state={state}
      data-testid="session-card-rating"
      className="session-card session-card--rating"
    >
      {loading ? (
        <p className="session-card__empty session-card__empty--center" aria-live="polite">
          Loading…
        </p>
      ) : (
        <div className="rating-card">
          {/* ── My row ── */}
          <div className="rating-card__row">
            <span className="rating-card__label">{myLabel}</span>
            {me.submitted ? (
              <>
                <RatingBars value={me.score} ariaLabel={`Your rating: ${me.score}`} />
                {me.note && (
                  <RatingNote author={isReport ? 'report' : 'manager'}>
                    {me.note}
                  </RatingNote>
                )}
              </>
            ) : !disabled ? (
              <div className="rating-card__input">
                <RatingBars
                  value={pendingScore}
                  onChange={setPendingScore}
                  ariaLabel="Your session rating"
                />
                <textarea
                  className="rating-card__note-input"
                  placeholder="Add a note (optional, 280 chars)"
                  maxLength={280}
                  value={pendingNote}
                  onChange={(e) => setPendingNote(e.target.value)}
                  aria-label="Rating note (optional)"
                  rows={2}
                />
                <button
                  type="button"
                  className="rating-card__submit-btn"
                  onClick={handleSubmit}
                  disabled={!pendingScore || submitting}
                  aria-busy={submitting}
                >
                  {submitting ? 'Submitting…' : 'Submit rating'}
                </button>
                {submitError && (
                  <p className="rating-card__error" role="alert">
                    {submitError}
                  </p>
                )}
              </div>
            ) : (
              <p className="session-card__empty">Not yet rated.</p>
            )}
          </div>

          {/* ── Partner row — covered until both have rated ── */}
          <div className="rating-card__row">
            <span className="rating-card__label">{partnerLabel}</span>
            <RevealCover
              revealed={revealed}
              label={partner.submitted ? 'awaiting reveal…' : 'awaiting their rating'}
            >
              {revealed ? (
                <>
                  <RatingBars value={partner.score} ariaLabel={`${partnerLabel} rating: ${partner.score}`} />
                  {partner.note && (
                    <RatingNote author={isReport ? 'manager' : 'report'}>
                      {partner.note}
                    </RatingNote>
                  )}
                </>
              ) : null}
            </RevealCover>
          </div>
        </div>
      )}
    </CardFrame>
  )
}
